import { useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface IUploaderOptions {
    // file upload endpoint
    endpoint: string;

    // file upload authentication token, automatically refreshes and adds to header if passed in
    getAuthToken: () => Promise<string>;

    // maximum number of simultaneous file uploads
    maxSimultaneousUploads?: number;

    events: {
        // true if the file is valid, false otherwise and don't add to uploads
        onFileValidation?: (fileName: string) => boolean | Promise<boolean>;

        // report file upload progress
        onProgress?: (id: string, fileName: string, progress: number) => void;

        // report file upload error
        onError?: (id: string, fileName: string, error: Error) => void;

        // report file upload success
        onComplete?: (id: string, fileName: string, response: any) => void;

        // report file upload start
        onStart?: (id: string, fileName: string, fileSizeKb: number) => void;

        // report all uploads completed
        onAllComplete?: (succeededFileIds: string[], failedFileIds: string[]) => void;
    };
}

interface IFile {
    id: string;
    name: string;
    size: number;
    progress: number;
}

export interface IFileUploader {
    upload: (files: File[] | FileList) => string[];
    cancel: (id?: string) => void;
    files: IFile[];
}

export const useFileUploader = (props: IUploaderOptions): IFileUploader => {
    const { endpoint, getAuthToken, events, maxSimultaneousUploads } = props;
    const { onFileValidation, onProgress, onError, onComplete, onStart, onAllComplete } = events;

    const [files, setFiles] = useState<IFile[]>([]);
    const requestsRef = useRef<Record<string, XMLHttpRequest>>({});
    const activeUploadsRef = useRef<number>(0);
    const queueRef = useRef<{ id: string; file: File }[]>([]);
    const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB

    // helper functions
    const checkAllCompleted = () => {
        if (activeUploadsRef.current === 0 && Object.keys(requestsRef.current).length > 0 && queueRef.current.length === 0) {
            onAllComplete?.([], []); // TBD: properly track succeeded/failed if needed, but for now just preserving structure
            requestsRef.current = {};
        }
    };

    const processQueue = async () => {
        const maxSimultaneous = maxSimultaneousUploads || Number.MAX_SAFE_INTEGER;
        while (activeUploadsRef.current < maxSimultaneous && queueRef.current.length > 0) {
            const nextItem = queueRef.current.shift();
            if (nextItem) {
                // update active number of uploads immediately to reserve the slot
                activeUploadsRef.current++;

                // validate the file in processQueue function instead of on push
                if (onFileValidation) {
                    const isValid = await onFileValidation(nextItem.file.name);
                    if (!isValid) {
                        // if invalid, release the slot and try to process next item
                        activeUploadsRef.current--;
                        checkAllCompleted();
                        continue;
                    }
                }
                uploadFileInChunks(nextItem.file, nextItem.id);
            }
        }
    };

    // event functions
    const addProgressEvent = (xhr: XMLHttpRequest, file: IFile, chunkIndex: number, totalSize: number) => {
        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
                const uploadedBeforeThisChunk = chunkIndex * CHUNK_SIZE;
                const totalUploaded = uploadedBeforeThisChunk + event.loaded;
                const progress = Math.min(Math.round((totalUploaded / totalSize) * 100), 100);
                onProgress?.(file.id, file.name, progress);
            }
        });
    };

    const addErrorEvent = (xhr: XMLHttpRequest, id: string, fileName: string) => {
        xhr.addEventListener('error', () => {
            onError?.(id, fileName, new Error('Network error'));
            checkAllCompleted();
        });
    };

    const addAbortEvent = (xhr: XMLHttpRequest) => {
        xhr.addEventListener('abort', () => {
            // Abort events are handled via the activeUploadsRef decrementing in uploadFileInChunks
            // but we might need to check if everything is stopped
        });
    };

    const uploadFileInChunks = async (fileToUpload: File, id: string) => {
        // calculate chunk size
        const totalSize = fileToUpload.size;
        const totalChunks = Math.max(1, Math.ceil(totalSize / CHUNK_SIZE));

        // add file to local array
        const file = { id, name: fileToUpload.name, size: totalSize, progress: 0 };
        setFiles((prev) => [...prev, file]);

        // report file upload start
        onStart?.(id, fileToUpload.name, totalSize / 1024);

        // track if file is cancelled
        let isCancelled = false;

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            if (isCancelled) break;

            // set up chunk props
            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, totalSize);
            const chunk = fileToUpload.slice(start, end);
            const chunkSize = end - start;

            const success = await new Promise<boolean>(async (resolve) => {
                // create XHR request for upload and add to requests array
                const xhr = new XMLHttpRequest();
                requestsRef.current[id] = xhr;

                // add event listeners for progress, error, and abort
                addProgressEvent(xhr, file, chunkIndex, totalSize);
                addErrorEvent(xhr, id, fileToUpload.name);
                addAbortEvent(xhr);

                // add event listener for load in order to determine if upload is complete
                xhr.addEventListener('load', () => {
                    delete requestsRef.current[id];
                    if (xhr.status >= 200 && xhr.status < 300) {
                        if (chunkIndex === totalChunks - 1) {
                            let response = xhr.response;
                            try {
                                response = JSON.parse(xhr.responseText);
                            } catch (e) {
                                // ignore parse error, use raw response
                            }
                            onComplete?.(id, fileToUpload.name, response);
                        }
                        resolve(true);
                    } else {
                        onError?.(id, fileToUpload.name, new Error(xhr.statusText || 'Upload failed'));
                        resolve(false);
                    }
                });

                xhr.addEventListener('error', () => {
                    delete requestsRef.current[id];
                });

                xhr.addEventListener('abort', () => {
                    delete requestsRef.current[id];
                    isCancelled = true;
                    resolve(false);
                });

                // get auth token and open XHR request
                const token = await getAuthToken();
                xhr.open('POST', endpoint);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);

                // append form data to chunk upload
                const formData = new FormData();
                formData.append('qqfile', chunk);
                formData.append('qqfilename', fileToUpload.name);
                formData.append('qqpartindex', chunkIndex.toString());
                formData.append('qqpartbyteoffset', start.toString());
                formData.append('qqchunksize', chunkSize.toString());
                formData.append('qqtotalfilesize', totalSize.toString());

                // send XHR request
                xhr.send(formData);
            });

            if (!success) break;
        }

        activeUploadsRef.current--;
        processQueue();
        checkAllCompleted();
    };

    // public functions
    const upload = (filesToUpload: File[] | FileList): string[] => {
        const fileIds: string[] = [];
        const filesToProcess = filesToUpload instanceof FileList ? Array.from(filesToUpload) : filesToUpload;

        for (const fileToUpload of filesToProcess) {
            // generate unique id
            const id = uuidv4();

            // add file id to local array
            fileIds.push(id);

            // add to queue
            queueRef.current.push({ id, file: fileToUpload });
        }

        // trigger queue processing
        processQueue();

        return fileIds;
    };

    const cancel = (id?: string) => {
        if (id) {
            // Cancel specific file
            if (requestsRef.current[id]) {
                requestsRef.current[id].abort();
                delete requestsRef.current[id];
            }
            // Also remove from queue if not started yet
            queueRef.current = queueRef.current.filter((f) => f.id !== id);
        } else {
            // Cancel all
            Object.values(requestsRef.current).forEach((xhr) => xhr.abort());
            requestsRef.current = {};
            queueRef.current = [];
        }
    };

    return {
        upload,
        cancel,
        files
    };
};
